import React, { useEffect, useState } from 'react'
import { Row, Col, Modal, Button } from 'antd'
import { LogoutOutlined } from '@ant-design/icons'
import { observer } from 'mobx-react-lite'
import { useNavigate } from 'react-router-dom'
import { contexts } from '@/domains'
import { globalStore } from '@/GlobalStore'

import '@/styles/pages/join.less'
import {
  JoinBannerConfig,
  JoinBrandConfig,
  JoinCardConfig,
  JoinCardIconSrc,
  JoinHeadingConfig,
  JoinTipContent,
  JoinTipModalConfig,
  JoinTipTitle,
  type JoinTipKey,
} from './Config'

const { GlobalContext } = contexts

const joinCardStyle = {
  width: JoinCardConfig.widthPx,
  height: JoinCardConfig.heightPx,
} as const

const joinPageStyle = {
  '--join-brand-logo-size': `${JoinBrandConfig.logoSizePx}px`,
  '--join-brand-logo-margin-right': `${JoinBrandConfig.logoMarginRightPx}px`,
  '--join-brand-name-font-size': `${JoinBrandConfig.nameFontSizePx}px`,
  '--join-brand-desc-font-size': `${JoinBrandConfig.descFontSizePx}px`,
  '--join-brand-to-content-gap': `${JoinBrandConfig.toContentGapPx}px`,
  '--join-card-icon-margin-right': `${JoinCardConfig.iconMarginRightPx}px`,
  '--join-card-title-subtitle-gap': `${JoinCardConfig.titleSubtitleGapPx}px`,
  '--join-banner-image-width': `${JoinBannerConfig.imageWidthPx}px`,
  '--join-heading-to-first-card-gap': `${JoinHeadingConfig.toFirstCardGapPx}px`,
} as React.CSSProperties

const joinCardIconStyle = {
  height: JoinCardConfig.iconHeightPx,
} as const

const Join = () => {
  const navigate = useNavigate()
  const [activeTipKey, setActiveTipKey] = useState<JoinTipKey | null>(null)
  const { getUserInfo, userInfo } = GlobalContext.useContainer()

  const closeTipModal = () => setActiveTipKey(null)
  const openTipModal = (key: JoinTipKey) => setActiveTipKey(key)
  useEffect(() => {
    const userId = userInfo?.id
    if (!userId) {
      return
    }
    void (async () => {
      const res = await getUserInfo(userId)
      if (res?.teamList?.length) {
        globalStore.syncCurrentTeamFromUser()
        await globalStore.loadTeamData()
        navigate('/home')
      }
    })()
  }, [userInfo?.id, getUserInfo, navigate])
  const handleLogout = () => {
    navigate('/login')
  }

  return (
    <div className="sparrow-wrap join" style={joinPageStyle}>
      <div className="join-main">
        <div className="join-top-bar">
          <button
            type="button"
            className="join-logout"
            onClick={handleLogout}
            aria-label="退出登录"
          >
            <LogoutOutlined className="join-logout-icon" />
            <span>退出登录</span>
          </button>
        </div>
        <div className="join-brand">
          <span className="join-brand-logo" aria-hidden />
          <div className="flex flex-col">
            <span className="join-brand-name">麻雀</span>
            <span className="join-brand-desc">专业远程交付管理系统</span>
          </div>
        </div>
        <Row className="sparrow-inner">
        <Col span={12} className="join-col">
          <div className="join-pane">
            <div className="join-pane-inner">
              <h1 style={{ fontSize: JoinHeadingConfig.fontSizePx }}>
                第一次使用麻雀系统? 你可以:
              </h1>
              <a
                className="join-btn"
                href="#"
                style={joinCardStyle}
                onClick={(e) => {
                  e.preventDefault()
                  openTipModal('joinExisting')
                }}
              >
                <img
                  src={JoinCardIconSrc.joinExisting}
                  alt=""
                  style={joinCardIconStyle}
                />
                <div style={{ fontSize: JoinCardConfig.titleFontSizePx }}>
                  {JoinTipTitle.joinExisting}
                  <span style={{ fontSize: JoinCardConfig.subtitleFontSizePx }}>
                    联系团队管理员添加自己到组织中
                  </span>
                </div>
              </a>
              <a
                className="join-btn"
                href="#"
                style={joinCardStyle}
                onClick={(e) => {
                  e.preventDefault()
                  openTipModal('createTemp')
                }}
              >
                <img
                  src={JoinCardIconSrc.createTemp}
                  alt=""
                  style={joinCardIconStyle}
                />
                <div style={{ fontSize: JoinCardConfig.titleFontSizePx }}>
                  {JoinTipTitle.createTemp}
                  <span style={{ fontSize: JoinCardConfig.subtitleFontSizePx }}>
                    适合多人承接中大型项目时使用
                  </span>
                </div>
              </a>
              <a
                className="join-btn"
                href="#"
                style={joinCardStyle}
                onClick={(e) => {
                  e.preventDefault()
                  openTipModal('createEnterprise')
                }}
              >
                <img
                  src={JoinCardIconSrc.createEnterprise}
                  alt=""
                  style={joinCardIconStyle}
                />
                <div style={{ fontSize: JoinCardConfig.titleFontSizePx }}>
                  {JoinTipTitle.createEnterprise}
                  <span style={{ fontSize: JoinCardConfig.subtitleFontSizePx }}>
                    圆领自营付费客户可以免费使用
                  </span>
                </div>
              </a>
            </div>
          </div>
        </Col>
        <Col span={12} className="join-col join-col-right">
          <div className="join-pane">
            <div className="join-pane-inner">
              <img
                className="join-banner-image"
                src={JoinBannerConfig.imageSrc}
                alt=""
              />
              <p className="join-banner-text">
                 根据实践总结，使用麻雀的远程团队的工作效率是传统坐班的1.5-2.5倍。
              </p>
            </div>
          </div>
        </Col>
        </Row>
      </div>
      <Modal
        className="join-tip-modal"
        open={activeTipKey !== null}
        centered
        destroyOnHidden
        width={JoinTipModalConfig.widthPx}
        title={
          activeTipKey ? (
            <span
              className="join-tip-modal-title"
              style={{ fontSize: JoinTipModalConfig.titleFontSizePx }}
            >
              {JoinTipTitle[activeTipKey]}
            </span>
          ) : undefined
        }
        onCancel={closeTipModal}
        styles={{
          content: {
            width: JoinTipModalConfig.widthPx,
            height: JoinTipModalConfig.heightPx,
            display: 'flex',
            flexDirection: 'column',
          },
          body: {
            flex: 1,
            overflowY: 'auto',
            fontSize: JoinTipModalConfig.bodyFontSizePx,
          },
        }}
        footer={
          <Button type="primary" onClick={closeTipModal}>
            我知道了
          </Button>
        }
      >
        {activeTipKey ? JoinTipContent[activeTipKey] : null}
      </Modal>
    </div>
  )
}

export default observer(Join)
